import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import FloatingPanel from '@/components/ui/FloatingPanel'
import MarketStatsTooltipContent from './MarketStatsTooltipContent'

const GAP = 8
const VIEWPORT_MARGIN = 16

/**
 * Hover anchor for the market stats panel. Wraps whatever figure opens it and floats
 * the same panel the card footer shows, portaled to <body>: a row is one of many
 * siblings, and a panel left inside it would be painted over by the next row on
 * scroll. Position is fixed, taken from the anchor's rect, and flips above when
 * there is no room below.
 */
const MarketStatsTooltip = ({ question, locale, children, className = '' }) => {
  const anchorRef = useRef(null)
  const panelRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)
  const [position, setPosition] = useState(null)

  const updatePosition = () => {
    if (!anchorRef.current) return
    const rect = anchorRef.current.getBoundingClientRect()
    /* offsetWidth/Height rather than a client rect: the panel animates in with a
       scale/translate, and a rect would measure the transformed, mid-animation box. */
    const panelHeight = panelRef.current?.offsetHeight || 0
    const panelWidth = panelRef.current?.offsetWidth || 0

    const placement = rect.bottom + panelHeight + GAP > window.innerHeight ? 'above' : 'below'
    const maxLeft = window.innerWidth - panelWidth - VIEWPORT_MARGIN
    const left = Math.max(VIEWPORT_MARGIN, Math.min(rect.left, maxLeft))

    setPosition({ left, placement, top: placement === 'below' ? rect.bottom : rect.top })
  }

  /* Layout effect: the panel is unmounted while closed, so the measurement taken on
     open sees no panel and cannot flip or clamp. Re-measuring before paint keeps the
     first frame from landing off-screen and then jumping into place. */
  useLayoutEffect(() => {
    if (!isOpen) return
    updatePosition()
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [isOpen])

  const open = () => {
    updatePosition()
    setIsOpen(true)
  }
  const close = () => setIsOpen(false)

  return (
    <span
      ref={anchorRef}
      className={className}
      onMouseEnter={open}
      onMouseLeave={close}
      onFocus={open}
      onBlur={close}>
      {children}
      {/* Mounted from the first hover on: a feed renders dozens of rows, and a panel
          per row costs a portal each before anyone has asked for one. Once opened it
          stays mounted so the close keeps its exit animation. */}
      {position !== null &&
        typeof document !== 'undefined' &&
        createPortal(
          <FloatingPanel
            ref={panelRef}
            isOpen={isOpen}
            className="w-[380px] max-w-[calc(100vw-32px)] p-4"
            style={
              position && {
                position: 'fixed',
                zIndex: 9999,
                left: position.left,
                top: position.placement === 'below' ? position.top + GAP : undefined,
                bottom: position.placement === 'above' ? window.innerHeight - position.top + GAP : undefined,
              }
            }>
            <MarketStatsTooltipContent question={question} locale={locale} />
          </FloatingPanel>,
          document.body
        )}
    </span>
  )
}

export default MarketStatsTooltip
